import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { map, take } from 'rxjs/operators';

import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.model';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { ClearSecurityRequest } from 'src/app/core/models/position-operations/clear-security-screening.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { SortCodeService } from 'src/app/core/services/common/sort-code/sort-code.service';
import { ClearSecurityScreeningService } from 'src/app/core/services/position-operations/clear-security-screening/clear-security-screening.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import {
  COMMON_SCREENING_METHOD_OPTION,
  CONST,
  SCREENING_METHOD_OPTION,
  SCREENING_TYPE_OPTION,
} from './security-screening-form.constant';

@Component({
  selector: 'app-security-screening-form',
  templateUrl: './security-screening-form.component.html',
  styleUrl: './security-screening-form.component.scss',
  standalone: false,
})
export class SecurityScreeningFormComponent implements OnInit {
  private readonly destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly _fb = inject(FormBuilder);
  private readonly _clearSecurityScreeningService = inject(
    ClearSecurityScreeningService,
  );
  private readonly _sortCodeService = inject(SortCodeService);
  private readonly _data = inject(DataUtils);

  readonly commonUtils = inject(CommonUtils);
  readonly CONST = { ...CONST };
  readonly SCREENING_TYPE_OPTION = SCREENING_TYPE_OPTION;
  readonly SCREENING_METHOD_OPTION = SCREENING_METHOD_OPTION;
  readonly COMMON_SCREENING_METHOD_OPTION = COMMON_SCREENING_METHOD_OPTION;
  readonly SpecialCharacters = SpecialCharacters;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE = IMAGE_SRC;

  securityScreeningForm!: FormGroup;
  currentDate: Date = new Date();
  minDate: Date = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    this.currentDate.getDate() - 1,
  );
  maxDate: Date = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    this.currentDate.getDate() + 3,
  );
  sortCodeOption: IOption[] = [];
  securityScreeningMethodsOption: IOption[] = [];
  commodityVerificationOption: IOption[] = [];

  ngOnInit(): void {
    this.getSortCodes();
    this.getSecurityScreeningMethods();
    this.getCommodityVerification();
    this.initForm();
    this.formValueChanges();
    this.subscribes();
  }

  /**
   * Init form with form controls
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  initForm() {
    this.securityScreeningForm = this._fb.group({
      screeningType: [SCREENING_TYPE_OPTION[0].value, [Validators.required]],
      screeningMethod: [null, [Validators.required]],
      screenerID: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(36)]),
      ],
      trackingNumber: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(18)]),
      ],
      secondaryScreening: [false],
      sortDate: [new Date(), [Validators.required]],
      sortCode: [null, [Validators.required]],
    });
  }

  /**
   * get form value changes
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  formValueChanges() {
    this.securityScreeningForm
      .get('screeningType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((screeningType) => {
        const isScreenTypeOne = screeningType === '04';
        if (isScreenTypeOne) {
          this.getFormControl('secondaryScreening')?.enable();
        } else {
          this.getFormControl('secondaryScreening')?.reset(false);
          this.getFormControl('secondaryScreening')?.disable();
        }
        const isCommonVerif = screeningType === '15';
        if (isCommonVerif) {
          this.getFormControl('screeningMethod')?.patchValue(null);
        }
      });
  }

  /**
   * get form control on specified field
   *
   * @param {string} field
   * @return {*}
   * @memberof SecurityScreeningFormComponent
   */
  getFormControl(field: string) {
    return this.securityScreeningForm.get(field);
  }

  subscribes() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(() => {
          this.resetForm();
        }),
      )
      .subscribe();
  }

  resetForm() {
    this.securityScreeningForm.reset({
      screeningType: SCREENING_TYPE_OPTION[0].value,
      screeningMethod: null,
      screenerID: '',
      sortCode: this.sortCodeOption?.[0]?.value,
      sortDate: new Date(),
      secondaryScreening: false,
    });
  }

  /**
   * submit form data
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  submit() {
    const formData = this.securityScreeningForm.value;

    const clearSecurityRequest: ClearSecurityRequest = {
      sortDate: formatDate(formData.sortDate, 'yyyy-MM-dd', 'en-US'),
      screeningType: formData.screeningType,
      screeningMethod: formData.screeningMethod,
      screenerName: formData.screenerID,
      trackingNumber: formData.trackingNumber,
      sortCode: formData.sortCode,
      secondaryScreening: formData.secondaryScreening || false,
    };

    const isCommodityScreening = formData.screeningType === '15';
    this.submitClearRequest(clearSecurityRequest, isCommodityScreening);
  }

  /**
   * submit clear request
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  submitClearRequest(request: ClearSecurityRequest, isCommodity: boolean) {
    const serviceCall = isCommodity
      ? this._clearSecurityScreeningService.submitClearCommodity(request)
      : this._clearSecurityScreeningService.submitClearSecurity(request);

    serviceCall
      .pipe(
        take(1),
        map((response) => {
          if (response.code == 200) {
            this.addDataToTable();
          }
        }),
      )
      .subscribe();
  }

  /**
   * add data to table
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  addDataToTable() {
    const screeningTypeValue = this.securityScreeningForm.value.screeningType;
    const screeningType = SCREENING_TYPE_OPTION.find(
      (o) => o.value === screeningTypeValue,
    )?.label;
    const screeningFormValue = {
      ...this.securityScreeningForm.value,
      screeningType,
    };
    const formData = { newSecurityScreen: screeningFormValue };
    this._data.newSecurityScreen$.next(formData);
    const formValue = this.securityScreeningForm.value;
    if (formValue) {
      this.securityScreeningForm.reset({
        screeningType: screeningTypeValue,
        screeningMethod: null,
        screenerID: this.securityScreeningForm.value.screenerID,
        sortCode: this.securityScreeningForm.value.sortCode,
        sortDate: this.securityScreeningForm.value.sortDate,
      });
      this.securityScreeningForm.markAsPristine();
      this.securityScreeningForm.markAsDirty();
      this.securityScreeningForm.markAsUntouched();
    }
  }

  /**
   * Used to get sort codes
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  private getSortCodes() {
    return this._sortCodeService
      .getSortCodes()
      .pipe(
        take(1),
        map((sortCodeResponse) => {
          const sortCodes = sortCodeResponse?.response?.sortCodes || [];
          if (sortCodes.length) {
            [...sortCodes].forEach((sortCode) => {
              const option = {
                label: sortCode.sorttype,
                value: sortCode.sorttype,
              };
              this.sortCodeOption.push(option);
            });
            this.securityScreeningForm
              .get('sortCode')
              ?.patchValue(sortCodes[0].sorttype);
            return;
          }

          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }),
      )
      .subscribe();
  }

  /**
   * Used to get sort codes
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  private getSecurityScreeningMethods() {
    return this._clearSecurityScreeningService
      .getSecurityScreeningMethods()
      .pipe(
        take(1),
        map((securityScreeningResponse) => {
          const methods = securityScreeningResponse?.response?.methods || [];
          if (methods.length) {
            [...methods].forEach((method) => {
              const option = {
                label: `${method.code}: ${method.shortText}`,
                value: method.code,
              };
              this.securityScreeningMethodsOption.push(option);
            });
            return;
          }

          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }),
      )
      .subscribe();
  }

  /**
   * Used to get commodity Verification
   *
   * @private
   * @memberof SecurityScreeningFormComponent
   */
  private getCommodityVerification() {
    return this._clearSecurityScreeningService
      .getCommodityVerification()
      .pipe(
        take(1),
        map((commodityVerificationResponse) => {
          const methods =
            commodityVerificationResponse?.response?.methods || [];
          if (methods.length) {
            [...methods].map((method) => {
              const option = {
                label: `${method.code}: ${method.shortText}`,
                value: method.code,
              };
              this.commodityVerificationOption.push(option);
            });
            return;
          }
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }),
      )
      .subscribe();
  }
}
